// Presentation layer for Zoom accounts & the host pool (EPIC E1). The wire
// vocabulary is frozen lowercase-underscore strings owned by the BE; the
// labels and colours are owned here, by the admin app. The settings page, the
// hosts card and the batch-management Zoom host tab all import from this file,
// so a state can never be amber in one place and red in another.
//
// THE RULE FOR EVERY MAP BELOW: an unrecognised value is rendered verbatim,
// never dropped and never crashed on. The BE is allowed to widen its enums
// without waiting for an admin release (@types/zoom.ts:10-12).

import { apiMessage, isConflict } from '../reminders/reminderLabels';
import {
  HostBusyConflict,
  HostBusyDetail,
  HostCheckUnavailableDetail,
  HostRefusalCode,
  ZoomCapacityHour,
  ZoomClashConflict,
  ZoomClashSession,
  ZoomHostView,
} from 'src/@types/zoom';
import {
  LiveRoomsPresentDetail,
  RemindersAlreadySentDetail,
  StaffSessionZoom,
  StartBlockedDetail,
  ZoomReadinessView,
} from 'src/@types/liveClass';

export { apiMessage, isConflict };

/**
 * The BE's AllExceptionsFilter emits { error, message } and, for an exception
 * constructed with an ARRAY, additionally { messages: string[] } while leaving
 * `message` as Nest's derived class-name string ("Conflict Exception").
 * apiMessage() reads `message` first and would render that literal string, so
 * every multi-line Zoom error MUST come through here.
 *
 * Returns a non-empty array of complete sentences, always safe to render as a
 * <Stack> of <Typography variant="body2"> lines.
 */
export const zoomMessages = (err: any, fallback = 'Something went wrong'): string[] => {
  if (typeof err === 'string') return [err || fallback];
  const arr = err?.messages;
  if (Array.isArray(arr) && arr.length) return arr.filter(Boolean).map(String);
  const one = err?.message;
  // Guard against Nest's derived placeholder leaking into the UI. This release
  // of Nest builds it by splitting the class name on capitals
  // (http.exception.js initMessage), i.e. "Conflict Exception" — but the
  // unspaced "ConflictException" is matched too, so an upgrade that drops the
  // join cannot start showing class names to operators.
  if (
    typeof one === 'string' &&
    one &&
    !/^[A-Z][a-z]+( ?[A-Z][a-z]+)* ?Exception$/.test(one)
  ) {
    return [one];
  }
  return [fallback];
};

/**
 * The rich clash payload, when the BE sent one. Never required to render.
 * A `self_clash` (two classes of the same batch) is the same shape when it
 * carries its pair in `conflicts`.
 */
export const zoomClashDetail = (err: any) =>
  (err &&
    typeof err === 'object' &&
    err.detail &&
    (err.detail.kind === 'host_clash' ||
      (err.detail.kind === 'self_clash' && Array.isArray(err.detail.conflicts))))
    ? err.detail
    : null;

/**
 * The scheduling refusals that carry no `host_clash` payload but still have to
 * be READ, not toasted: two classes of the same batch collide (`self_clash`),
 * the clash read was too big to finish (`unknown_clash`), or a room could not
 * be deleted so nothing was replaced (`teardown_failed`). Returns the title and
 * the way out; the sentences are the BE's `messages[]`.
 */
export const scheduleRefusalOf = (err: any): { kind: string; title: string; hint: string } | null => {
  const kind = err && typeof err === 'object' ? err.detail?.kind || err.code : undefined;
  switch (kind) {
    case 'self_clash':
      // A cancelled class of this batch whose Zoom room Zoom has not
      // confirmed deleted still holds its time on the licence.
      if (err.detail?.other?.cancelledRoom) {
        return {
          kind,
          title: 'A cancelled class still holds this time',
          hint: 'Its Zoom room is still on the licence because Zoom has not confirmed the delete. Kayfa keeps retrying; pick another time, or try again once the room is gone. Nothing was changed.',
        };
      }
      return {
        kind,
        title: 'Two classes of this batch would overlap',
        hint: 'One Zoom licence runs one meeting at a time, and a batch’s classes share its licence. Move one of them. Nothing was changed.',
      };
    case 'unknown_clash':
      return {
        kind,
        title: 'Kayfa could not finish the clash check',
        hint: 'There were too many classes to check, so the change was refused rather than approved on an incomplete answer. Nothing was changed.',
      };
    case 'teardown_failed':
      return {
        kind,
        title: 'Nothing was replaced',
        hint: 'Zoom did not confirm that a room was deleted, so every class and room was kept — a room left on the licence would still block its slot. Try again in a few minutes.',
      };
    default:
      return null;
  }
};

/**
 * E2 §6.3 — the unpublish refusal. A class learners have already been reminded
 * about cannot be made to vanish silently; the only honest reversal is a cancel,
 * which tells them.
 */
export const remindersSentDetail = (err: any): RemindersAlreadySentDetail | null =>
  (err && typeof err === 'object' && err.detail && err.detail.kind === 'reminders_already_sent')
    ? (err.detail as RemindersAlreadySentDetail)
    : null;

/**
 * E2 §9.6 — the regenerate refusal. Regenerating deletes every session row, so
 * upcoming classes holding a live Zoom room would be left running on the host's
 * licence with nothing left that can reach or cancel them.
 */
export const liveRoomsDetail = (err: any): LiveRoomsPresentDetail | null =>
  (err && typeof err === 'object' && err.detail && err.detail.kind === 'live_rooms_present')
    ? (err.detail as LiveRoomsPresentDetail)
    : null;

/**
 * The per-session lock said no. Every E2 write can answer this, on any endpoint,
 * whenever another replica (or the preflight cron) is already touching the row.
 * It is "try again in a moment", never an error state — and never a reason to
 * create a second room by hand.
 */
export const isBusyConflict = (err: any): boolean =>
  isConflict(err) &&
  zoomMessages(err, '')
    .join(' ')
    .toLowerCase()
    .includes('another operation is already running');

// ----------------------------------------------------------------------
// Chips.

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';
type ChipVariant = 'filled' | 'outlined' | 'ghost';

export type ZoomChipStyle = { color: ChipColor; variant: ChipVariant };

const UNKNOWN_CHIP: ZoomChipStyle = { color: 'default', variant: 'outlined' };

/**
 * Title-case a wire value nobody has written copy for yet. `bad_credentials`
 * becomes "Bad credentials" rather than disappearing — a label the operator
 * can at least quote back to us beats a blank cell.
 */
const humanise = (value: string): string => {
  const spaced = value.replace(/[_-]+/g, ' ').trim();
  if (!spaced) return value;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// ── the derived account status (§11.1) ─────────────────────────────────────

// `failing` is the BE's word (zoom.view.ts:20-27). `error` is kept as an alias
// so a widening on either side still lands on a red chip rather than on the
// grey "we have never heard of this" default.
const ACCOUNT_STATUS_STYLE: Record<string, ZoomChipStyle> = {
  ok: { color: 'success', variant: 'ghost' },
  untested: { color: 'default', variant: 'outlined' },
  failing: { color: 'error', variant: 'ghost' },
  error: { color: 'error', variant: 'ghost' },
  disabled: { color: 'default', variant: 'ghost' },
  not_configured: { color: 'warning', variant: 'ghost' },
  key_unavailable: { color: 'error', variant: 'ghost' },
};

export const accountStatusStyle = (status: string): ZoomChipStyle =>
  ACCOUNT_STATUS_STYLE[status] || UNKNOWN_CHIP;

const ACCOUNT_STATUS_LABEL: Record<string, string> = {
  ok: 'Connected',
  untested: 'Not tested yet',
  failing: 'Test failed',
  error: 'Test failed',
  disabled: 'Disabled',
  not_configured: 'Encryption key missing',
  key_unavailable: 'Secret unreadable',
};

export const accountStatusLabel = (status: string): string =>
  ACCOUNT_STATUS_LABEL[status] || humanise(status);

// One line of plain English per derived status — the row tooltip. Each one
// says what is true AND what it costs, because "Disabled" alone does not tell
// an operator whether classes are still being served.
const ACCOUNT_STATUS_HELP: Record<string, string> = {
  ok: 'Kayfa reached this account and the required permissions are present.',
  untested: 'Connected but never tested. Press Test connection before importing hosts.',
  failing: 'The last test failed. Open it to see what Zoom said and how to fix it.',
  error: 'The last test failed. Open it to see what Zoom said and how to fix it.',
  disabled: 'Switched off here. Its hosts cannot be assigned to anything new.',
  not_configured:
    'ZOOM_ENC_KEY is not set on the API, so no stored secret can be read. Existing assignments are untouched.',
  key_unavailable:
    'The stored secret cannot be decrypted with the key the API currently holds. Re-enter the client secret, or restore the previous key.',
};

export const accountStatusHelp = (status: string): string =>
  ACCOUNT_STATUS_HELP[status] || '';

// ── test / import reasons (§7.1) ───────────────────────────────────────────

const TEST_REASON_LABEL: Record<string, string> = {
  ok: 'Connected',
  disabled: 'Integration switched off',
  not_configured: 'Encryption key missing',
  key_unavailable: 'Secret unreadable',
  bad_credentials: 'Credentials rejected',
  not_activated: 'App not activated',
  insufficient_scopes: 'Missing permission',
  rate_limited: 'Rate limited',
  zoom_unavailable: 'Zoom server error',
  timeout: 'Timed out',
  network_error: 'Could not reach Zoom',
  unknown: 'Unexpected response',
};

export const testReasonLabel = (reason?: string | null): string => {
  if (!reason) return '—';
  return TEST_REASON_LABEL[reason] || humanise(reason);
};

const TEST_REASON_STYLE: Record<string, ZoomChipStyle> = {
  ok: { color: 'success', variant: 'ghost' },
  disabled: { color: 'default', variant: 'ghost' },
  not_configured: { color: 'warning', variant: 'ghost' },
  key_unavailable: { color: 'error', variant: 'ghost' },
  bad_credentials: { color: 'error', variant: 'ghost' },
  not_activated: { color: 'warning', variant: 'ghost' },
  insufficient_scopes: { color: 'warning', variant: 'ghost' },
  rate_limited: { color: 'warning', variant: 'ghost' },
  zoom_unavailable: { color: 'error', variant: 'ghost' },
  timeout: { color: 'error', variant: 'ghost' },
  network_error: { color: 'error', variant: 'ghost' },
  unknown: { color: 'error', variant: 'outlined' },
};

export const testReasonStyle = (reason?: string | null): ZoomChipStyle =>
  (reason && TEST_REASON_STYLE[reason]) || UNKNOWN_CHIP;

/**
 * True for the reasons where NO request left the API process. Mirrors
 * isLocalReason() in the BE's zoom.errors.ts. The test dialog uses it to stop
 * promising a tracking id that cannot exist — there is no Zoom response to
 * have one.
 */
export const isLocalTestReason = (reason?: string | null): boolean =>
  reason === 'disabled' || reason === 'not_configured' || reason === 'key_unavailable';

// ── host state (§3.2) ──────────────────────────────────────────────────────

const HOST_STATE_LABEL: Record<string, string> = {
  active: 'Active',
  unlicensed: 'Not licensed',
  pending: 'Not activated',
  disabled: 'Paused',
  missing: 'Gone from Zoom',
};

export const hostStateLabel = (state: string): string =>
  HOST_STATE_LABEL[state] || humanise(state);

const HOST_STATE_STYLE: Record<string, ZoomChipStyle> = {
  active: { color: 'success', variant: 'ghost' },
  // 'Paused' was a grey ghost chip and read as "nothing to see here". An
  // operator paused the only licence on the account and could not tell from the
  // row that they had done it. Warning + filled, so the one state a human
  // caused is the one state that stands out.
  unlicensed: { color: 'warning', variant: 'ghost' },
  pending: { color: 'warning', variant: 'ghost' },
  disabled: { color: 'warning', variant: 'filled' },
  missing: { color: 'error', variant: 'ghost' },
};

export const hostStateStyle = (state: string): ZoomChipStyle =>
  HOST_STATE_STYLE[state] || UNKNOWN_CHIP;

const HOST_STATE_HELP: Record<string, string> = {
  active: 'Licensed and available to assign.',
  unlicensed:
    'Zoom reports this user as Basic, not Licensed. A Basic host is cut off after 40 minutes, so it cannot run a class. Assign the licence in Zoom and import again.',
  pending:
    'This user has not activated their Zoom account yet, so no meeting can be started on it. Chase the invitation, then import again.',
  disabled: 'Parked by an operator. Existing bookings still hold; nothing new can be assigned.',
  missing:
    'This host stopped coming back from Zoom on the last import — the licence was probably removed. Any batch still pointing at it needs re-homing.',
};

export const hostStateHelp = (state: string): string => HOST_STATE_HELP[state] || '';

/**
 * A host that cannot serve anything, whatever the candidate batch is.
 *
 * The BE ships its own verdict as `usable`; it is the same rule (state ===
 * 'active') and it is preferred so a sixth state added there does not have to
 * be guessed at here.
 */
export const isHostUsable = (host: ZoomHostView): boolean =>
  typeof host?.usable === 'boolean' ? host.usable : host?.state === 'active';

// ── why a host cannot take this batch (§11.9) ──────────────────────────────

const UNASSIGNABLE_LABEL: Record<string, string> = {
  clash: 'Busy — already booked over these class times',
  unknown_clash:
    'Too many classes to check — refused rather than approved on an incomplete answer',
  host_unlicensed: 'This host is not a licensed Zoom user',
  host_pending: 'This host has not activated their Zoom account',
  host_disabled: 'This host is parked',
  host_missing: 'This host is gone from Zoom',
  account_disabled: 'Its Zoom account is switched off',
  account_not_configured: 'Its Zoom account has no readable credentials',
  already_assigned: 'Already assigned to this batch',
  // HOST-EXCLUSIVE R3z — Zoom lets a no-fixed-time meeting start at any
  // moment, so nothing can be booked on that licence while it exists.
  zoom_no_fixed_time: 'Has a Zoom meeting with no fixed time — it can start at any moment',
  host_check_unavailable: 'Kayfa could not ask Zoom whether this host is free',
};

export const unassignableLabel = (reason?: string | null): string => {
  if (!reason) return '';
  return UNASSIGNABLE_LABEL[reason] || humanise(reason);
};

/**
 * The BE sends `unassignableReasons` — an ARRAY, because a host can be both
 * parked AND on a switched-off account, and telling an operator only the first
 * of the two sends them to fix the wrong thing. The singular spelling is read
 * as a fallback so a rename on either side cannot silently blank the column.
 */
export const unassignableReasonsOf = (host?: Partial<ZoomHostView> | null): string[] => {
  const many = host?.unassignableReasons;
  if (Array.isArray(many) && many.length) return many.filter(Boolean).map(String);
  const one = (host as any)?.unassignableReason;
  return one ? [String(one)] : [];
};

/** Every reason, as one human sentence. '' when the host IS assignable. */
export const unassignableText = (host: ZoomHostView): string =>
  unassignableReasonsOf(host).map(unassignableLabel).filter(Boolean).join(' · ');

/**
 * The tooltip for a greyed picker row. Never returns '' for an unassignable
 * host — a disabled row with no explanation is the single most common way an
 * operator concludes the screen is broken.
 */
export const unassignableHelp = (host: ZoomHostView): string => {
  if (host.assignable !== false) return '';
  const text = unassignableText(host) || 'This host cannot take this batch. The API did not say why.';
  // HOST-EXCLUSIVE — say WHY a clash is a refusal and not a warning, and that
  // a room still on the licence (a moved or cancelled class whose delete Zoom
  // has not confirmed) counts, or the operator goes looking for a class that
  // is no longer on any timetable.
  return unassignableReasonsOf(host).includes('clash')
    ? `${text}. One Zoom licence runs one meeting at a time, so a host that already has a class — or a room still on the licence — over any of this batch’s class times cannot take it.`
    : text;
};

// ── capacity (§10) ─────────────────────────────────────────────────────────

// The BE's word is `over`; both spellings map to the same red cell.
const CAPACITY_STATUS_LABEL: Record<string, string> = {
  ok: 'OK',
  tight: 'Tight',
  over: 'Oversubscribed',
  oversubscribed: 'Oversubscribed',
};

export const capacityStatusLabel = (status?: string | null): string => {
  if (!status) return '—';
  return CAPACITY_STATUS_LABEL[status] || humanise(status);
};

/**
 * How many usable hosts this bucket had. The BE calls it `capacity`; the older
 * spelling was `available`.
 *
 * THE `?? ` IS LOAD-BEARING, and `||` here would be a bug: a genuine 0 must
 * survive as 0, and a MISSING field must not silently become 0 either — that is
 * what made every hour on the report read "oversubscribed" when the field was
 * being looked for under the wrong name. Absent stays undefined, and the
 * grader below refuses to grade rather than inventing a shortfall.
 */
export const capacityAvailable = (hour: ZoomCapacityHour): number | undefined => {
  const raw = hour?.capacity ?? hour?.available;
  const n = Number(raw);
  return raw === undefined || raw === null || Number.isNaN(n) ? undefined : n;
};

/**
 * The bucket's verdict.
 *
 * The BE's own `status` wins whenever we recognise it — it is computed from the
 * same two numbers and it is the value the operator sees quoted in support
 * threads. We only recompute when the wire says nothing we understand, and only
 * when we actually HAVE both numbers: colouring an hour red because a field
 * arrived under a different name is a false alarm at maximum volume, and an
 * operator who is shown one learns to ignore the card.
 */
export const capacityHourStatus = (hour: ZoomCapacityHour): string => {
  const wire = hour?.status;
  if (wire && CAPACITY_STATUS_LABEL[wire]) return wire === 'oversubscribed' ? 'over' : wire;
  const available = capacityAvailable(hour);
  if (available === undefined) return wire || 'ok';
  const needed = Number(hour?.needed) || 0;
  if (needed > available) return 'over';
  if (needed > 0 && needed === available) return 'tight';
  return wire || 'ok';
};

/** True only for the red verdict, under either spelling. */
export const isOversubscribed = (status: string): boolean =>
  status === 'over' || status === 'oversubscribed';

type CapacityTone = { color: string; bg: string };

/**
 * Palette keys rather than literals so the cells follow the theme (and dark
 * mode) the way every other coloured surface in the app does.
 */
export const capacityTone = (status: string): CapacityTone => {
  switch (status) {
    case 'over':
    case 'oversubscribed':
      return { color: 'error.dark', bg: 'error.lighter' };
    case 'tight':
      return { color: 'warning.dark', bg: 'warning.lighter' };
    case 'ok':
      return { color: 'success.dark', bg: 'success.lighter' };
    default:
      return { color: 'text.secondary', bg: 'background.neutral' };
  }
};

// ----------------------------------------------------------------------
// Formatting.

/**
 * The BE pre-renders `hourLabel` in the report's timezone. Print it verbatim
 * whenever it is there — re-deriving it from the ISO instant in the browser's
 * zone is how the capacity report and the timetable start disagreeing about
 * which hour is full.
 */
export const hourText = (hour: ZoomCapacityHour): string => {
  const label = hour?.hourLabel || hour?.hourLocal;
  if (label) return label;
  const instant = hour?.hourStart || hour?.hour;
  if (!instant) return '—';
  const d = new Date(instant);
  return Number.isNaN(d.getTime())
    ? String(instant)
    : d.toISOString().slice(0, 16).replace('T', ' ');
};

/**
 * A stable React key for one bucket. `hourStart` is unique per row; falling
 * back to the label (and then to the index at the call site) keeps two rows
 * from colliding on `undefined`, which is how a keyed list starts reusing the
 * wrong cells.
 */
export const hourKey = (hour: ZoomCapacityHour, index: number): string =>
  String(hour?.hourStart || hour?.hour || hour?.hourLabel || hour?.hourLocal || index);

/** "Mon 18:00" for one class, from whatever the clash payload carried. */
export const sessionWhen = (s?: ZoomClashSession | null): string => {
  if (!s?.startsAt) return '';
  const d = new Date(s.startsAt);
  return Number.isNaN(d.getTime()) ? String(s.startsAt) : d.toLocaleString();
};

/** "Diploma — Batch" for one side of a clash. */
export const sessionCohort = (s?: ZoomClashSession | null): string =>
  [s?.diplomaName || s?.diplomaId, s?.batchName].filter(Boolean).join(' — ');

/**
 * One colliding pair as a sentence — the same words ZoomClashDialog uses for a
 * row of the assign 409, for the picker's greyed row (LVH-F5).
 */
export const clashPairLine = (c: ZoomClashConflict): string => {
  const mine = `“${c.candidate?.title || 'This batch’s class'}”${
    sessionWhen(c.candidate) ? ` at ${sessionWhen(c.candidate)}` : ''
  }`;
  const theirs = `“${c.held?.title || 'a class'}”${
    sessionCohort(c.held) ? ` (${sessionCohort(c.held)})` : ''
  }${sessionWhen(c.held) ? ` at ${sessionWhen(c.held)}` : ''}`;
  const tail =
    c.held?.source === 'room_on_host'
      ? ' — its Zoom room is still on this licence'
      : c.kind === 'buffer' && c.gapMinutes !== undefined
      ? ` — only ${c.gapMinutes} minutes between them`
      : c.overlapMinutes
      ? ` — ${c.overlapMinutes} minutes of overlap`
      : '';
  return `${mine} ${c.kind === 'buffer' ? 'is too close to' : 'clashes with'} ${theirs}${tail}.`;
};

/** The picker row's colliding pairs, one per line, with "…and N more". */
export const clashPairLines = (host?: Partial<ZoomHostView> | null): string[] => {
  const pairs = Array.isArray(host?.clashes) ? host!.clashes : [];
  const lines = pairs.map(clashPairLine);
  const more = Number(host?.clashCount || 0) - pairs.length;
  if (lines.length && more > 0) lines.push(`…and ${more} more.`);
  return lines;
};

/** A host's display name for a picker row, falling back through what exists. */
export const hostLabel = (host?: Partial<ZoomHostView> | null): string =>
  (host?.displayName || host?.email || host?.zoomUserId || host?.id || 'Unknown host') as string;

/**
 * A batch-scope assignment has a batchName; a diploma-scope SHORTLIST entry
 * does not. The BE stores the shortlist as batchName: '' (§11.11), and '' is
 * falsy, so a plain truthiness test is the whole rule.
 */
export const isShortlistEntry = (batchName?: string | null): boolean => !batchName;

// ----------------------------------------------------------------------
// EPIC E2 — the live classroom (§2.2, §10, §13.3).
//
// Same rule as everything above: an unrecognised value is humanised and shown,
// never dropped and never crashed on.

// ── the session's Zoom room state (§2.2) ───────────────────────────────────

const ZOOM_SESSION_STATE_LABEL: Record<string, string> = {
  none: 'No room',
  pending: 'Creating…',
  created: 'Room ready',
  stale: 'Old time in Zoom',
  failed: 'Needs attention',
  cancelled: 'Cancelled',
};

export const zoomSessionStateLabel = (state?: string | null): string => {
  if (!state) return '—';
  return ZOOM_SESSION_STATE_LABEL[state] || humanise(state);
};

// `none` is DELIBERATELY neutral, not amber. Most of the time it means "the
// class is more than three days out and the room has not been minted yet",
// which is the designed steady state — colouring it as a problem would put a
// warning on every healthy schedule and teach operators to ignore the column.
const ZOOM_SESSION_STATE_STYLE: Record<string, ZoomChipStyle> = {
  none: { color: 'default', variant: 'outlined' },
  pending: { color: 'info', variant: 'ghost' },
  created: { color: 'success', variant: 'ghost' },
  stale: { color: 'warning', variant: 'ghost' },
  failed: { color: 'error', variant: 'ghost' },
  cancelled: { color: 'default', variant: 'ghost' },
};

export const zoomSessionStateStyle = (state?: string | null): ZoomChipStyle =>
  (state && ZOOM_SESSION_STATE_STYLE[state]) || UNKNOWN_CHIP;

const ZOOM_SESSION_STATE_HELP: Record<string, string> = {
  none: 'No Zoom room has been reserved for this class yet.',
  pending:
    'Kayfa has claimed the right to create this room and is waiting for Zoom to confirm. It resolves itself within a few minutes — never create a second room by hand, that is how one class ends up with two.',
  created: 'A Zoom room exists and its start time matches this class.',
  stale:
    'This class was moved and Zoom still has the old time. Kayfa re-sends it automatically every couple of minutes; “Re-send the time” does it now.',
  failed:
    'Zoom refused, or could not be reached. The reason is on this row, and retrying is safe — a room is never created twice.',
  cancelled:
    'This class was cancelled and its Zoom room was deleted. The meeting id is kept as the record of which licence ran it.',
};

export const zoomSessionStateHelp = (state?: string | null): string =>
  (state && ZOOM_SESSION_STATE_HELP[state]) || '';

// HOST-EXCLUSIVE (LVH-F3) — a `stale` row whose error is race_undo_failed was
// NOT moved: its room raced a meeting booked on the same licence, and Zoom did
// not confirm the undo. The generic stale help ("This class was moved…") is
// wrong for it, so the tooltip says what happened instead.
const RACE_UNDO_STATE_HELP =
  'This class was not moved. Right after its room was created, another meeting was booked on the same Zoom licence at the same time, and Zoom did not confirm the delete of the new room. The room is kept but cannot be started while the licence is busy; Kayfa checks the licence again by itself.';

/** The state line of a room's tooltip, aware of why a `stale` room is stale. */
export const zoomRoomStateHelp = (zoom?: { state?: string | null; error?: { reason?: string } | null } | null): string =>
  zoom?.state === 'stale' && zoom?.error?.reason === 'race_undo_failed'
    ? RACE_UNDO_STATE_HELP
    : zoomSessionStateHelp(zoom?.state);

// ── why (§10, the complete gate table) ─────────────────────────────────────
//
// EVERY ROW OF §10 PRODUCES AN EXPLANATION, AND NONE OF THEM IS AN ERROR.
// Missing Zoom is a fact about the environment, not a failure of this class:
// scheduling, generating, editing, publishing and cancelling all work with zero
// Zoom accounts connected. That is why these read as statements rather than
// alarms, and why the four E1 rows below are reused VERBATIM through the E1
// helpers rather than re-worded — an operator must not be told two different
// things about one condition on two screens.

const ZOOM_SESSION_REASON_HELP: Record<string, string> = {
  live_disabled:
    'Live classrooms are switched off in this environment. Classes still run; there is no online room.',
  zoom_disabled:
    'Zoom is switched off in this environment. Classes still run; there is no online room.',
  not_configured: ACCOUNT_STATUS_HELP.not_configured,
  no_account:
    'No Zoom account is connected. You can schedule and publish classes now; rooms will be created once an admin connects Zoom.',
  account_disabled: ACCOUNT_STATUS_HELP.disabled,
  no_host:
    'No Zoom host is assigned to this batch, so no room can be reserved. Assign one in the Zoom host tab.',
  host_unlicensed: HOST_STATE_HELP.unlicensed,
  host_pending: HOST_STATE_HELP.pending,
  host_disabled: HOST_STATE_HELP.disabled,
  host_missing: HOST_STATE_HELP.missing,
  not_published: 'Publish the class first — a draft class has no room.',
  in_the_past: 'This class has already started.',
  budget_exhausted:
    'This Zoom host has reached its daily limit for creating meetings. Kayfa will try again after midnight UTC.',
  rate_limited:
    'Zoom is rate limiting this account. Kayfa has paused room creation until midnight UTC.',
  abandoned:
    'Zoom was asked for this room, nothing came back, and a follow-up check found no meeting. Kayfa will try again.',
  unknown_outcome:
    'Kayfa could not confirm with Zoom whether this room was created. Check the Zoom portal before creating a new one.',
  zoom_error: 'Zoom refused this request. The details are on the row.',
  batch_paused:
    'Live classes for this cohort are paused, so no room is created or updated. Existing rooms are kept; resume the cohort to carry on.',
  // HOST-EXCLUSIVE R1 — one Zoom licence runs one meeting at a time.
  host_busy:
    'The Zoom host is busy at this time (another class or a meeting outside Kayfa). Kayfa checks again every 15 minutes — move the class or change the batch’s host. A room this class already has cannot be started until the host is free at its time.',
  host_check_failed:
    'Kayfa could not ask Zoom whether the host is free. It will try again in 5 minutes.',
};

/**
 * How far ahead a room is minted, as English. The BE ships `leadHours` on
 * ZoomReadinessView (LIVE_MEETING_LEAD_HOURS, clamped 25..336), so the sentence
 * always states the number this deployment actually uses rather than a default
 * that may have been turned.
 */
const leadText = (leadHours?: number): string => {
  const h = Number(leadHours);
  if (!Number.isFinite(h) || h <= 0) return 'a few days';
  if (h % 24 === 0) {
    const days = h / 24;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  return `${h} hour${h === 1 ? '' : 's'}`;
};

/**
 * §10 / D9 — the meeting scopes are a CAPABILITY, not a failure. Adding them to
 * E1's required set would have turned every already-connected account
 * `insufficient_scopes` and broken a working host import on an E2 feature, so
 * the banner is amber and everything E1 does stays green without them.
 */
export const ZOOM_MEETING_SCOPE_HELP =
  'Live classes need two more Zoom scopes: meeting:write:admin and meeting:read:admin. Add them in the Zoom App Marketplace, save, and re-activate the app.';

export const zoomSessionReasonHelp = (
  reason?: string | null,
  opts?: { leadHours?: number },
): string => {
  if (!reason) return '';
  if (reason === 'too_far_ahead') {
    return `The room is created ${leadText(opts?.leadHours)} before the class.`;
  }
  return ZOOM_SESSION_REASON_HELP[reason] || humanise(reason);
};

/**
 * THE ONE SENTENCE A DISABLED "Create the room" BUTTON HANGS OFF.
 *
 * '' means the environment can mint a room right now. Anything else is the
 * reason it cannot, in a form an operator can act on. The BE's own `reason` wins
 * whenever it sent one — it is computed from the same facts and it is the string
 * quoted in support threads; the flag checks below only run when `reason` is
 * empty, so a BE that widens the vocabulary still produces a usable tooltip.
 *
 * A DISABLED CONTROL WITH NO EXPLANATION IS THE SINGLE MOST COMMON WAY AN
 * OPERATOR CONCLUDES A SCREEN IS BROKEN. This function is why that cannot
 * happen here.
 */
export const readinessBlocker = (zoom?: ZoomReadinessView | null): string => {
  if (!zoom) return '';
  if (zoom.reason) return zoomSessionReasonHelp(zoom.reason, { leadHours: zoom.leadHours });
  if (zoom.enabled === false) return ZOOM_SESSION_REASON_HELP.live_disabled;
  if (zoom.configured === false) return ZOOM_SESSION_REASON_HELP.not_configured;
  if (zoom.accountConnected === false) return ZOOM_SESSION_REASON_HELP.no_account;
  if (zoom.hostAssigned === false) return ZOOM_SESSION_REASON_HELP.no_host;
  if (zoom.hostUsable === false) {
    return 'The Zoom host assigned to this batch cannot run a class. Pick a working host in the Zoom host tab.';
  }
  if (zoom.meetingsOk === false) return ZOOM_MEETING_SCOPE_HELP;
  return '';
};

// ── which room actions a row may be offered (§3, the correctness core) ─────
//
// THE UI IS NOT THE GUARANTEE — the BE's compare-and-swap is (its claim
// predicate is `zoomState ∈ {none, failed}`, so a stray create is a no-op). But
// there is exactly ONE admin action that reaches around that CAS, and these two
// predicates are what keep it away from the rows where it would do damage.

/**
 * MAY THIS ROW'S ROOM BE DELETED AND RE-CREATED — the "move to the new host"
 * action, and nothing else?
 *
 * That action is DELETE …/meeting followed by POST …/meeting. The delete clears
 * `zoomMeetingId` and drops the row to `zoomState: 'none'` with `zoomAttempts`
 * reset, which is correct for a row whose room we can see and CATASTROPHIC for a
 * row in `pending`:
 *
 *   `pending` means Kayfa POSTed to Zoom and does NOT yet know the answer. A
 *   real room may already exist that we hold no id for — that is the whole
 *   window §3.1 step 6c exists to cover. The reconciler resolves it by listing
 *   the host's meetings and matching the `agenda` token, but it only ever reaps
 *   rows that are still `pending`. Dropping such a row to `none` deletes nothing
 *   (there is no id to delete), makes that orphan permanently un-adoptable, and
 *   the create that follows mints a SECOND billed room for one class — two
 *   links, two bills, and a room nobody can cancel.
 *
 * `created` and `stale` are the only two states in which we can prove which room
 * we are about to delete, so they are the only two this returns true for.
 */
export const canMoveRoom = (state?: string | null): boolean =>
  state === 'created' || state === 'stale';

/**
 * May this row be OFFERED a "create the room" button right now?
 *
 * Pressing it on a `pending` row is harmless (the CAS refuses), but offering it
 * is dishonest: it promises a room when the truthful answer is "wait for Zoom,
 * and do not ask again". The health band's own `pending` copy says exactly that,
 * so the button must agree with it rather than contradict it two inches away.
 *
 * `unknown_outcome` is the same rule with more at stake — Kayfa could not
 * confirm with Zoom whether a room was created, and §3.4 is explicit that the
 * only correct next action is a human opening the Zoom portal.
 */
export const canCreateRoom = (zoom?: { state?: string; reason?: string } | null): boolean =>
  zoom?.state !== 'pending' &&
  zoom?.state !== 'cancelled' &&
  zoom?.reason !== 'unknown_outcome';

// ── the learner's side, rendered read-only (§5.2) ──────────────────────────

const JOIN_STATE_LABEL: Record<string, string> = {
  open: 'Open now',
  too_early: 'Not open yet',
  not_ready: 'No room yet',
  ended: 'Ended',
  cancelled: 'Cancelled',
  paused: 'Cohort paused',
};

export const joinStateLabel = (state?: string | null): string => {
  if (!state) return '—';
  return JOIN_STATE_LABEL[state] || humanise(state);
};

// ── formatting helpers for the room column ─────────────────────────────────

/**
 * Everything worth knowing about one class's room, as tooltip text. Ordered
 * most-actionable first, and it NEVER contains a URL: the join link is
 * select:false in the database and the start_url is a bearer credential fetched
 * per click. `meetingIdMasked` is the most a support conversation gets.
 */
export const zoomRoomTooltip = (
  zoom?: StaffSessionZoom | null,
  opts?: { leadHours?: number },
): string => {
  if (!zoom) return '';
  const lines = [
    zoomRoomStateHelp(zoom),
    zoomSessionReasonHelp(zoom.reason, opts),
    zoom.error?.title ? `${zoom.error.title} — ${zoom.error.message}` : '',
    zoom.drift ? 'Zoom reports a different start time for this room.' : '',
    zoom.meetingIdMasked ? `Meeting ${zoom.meetingIdMasked}` : '',
    zoom.hostEmail ? `Host ${zoom.hostEmail}` : '',
    Number(zoom.attempts) > 1 ? `${zoom.attempts} attempts so far` : '',
  ];
  return lines.filter(Boolean).join('\n');
};

// ----------------------------------------------------------------------
// HOST-EXCLUSIVE (§3.6) — ONE ZOOM LICENCE RUNS ONE MEETING AT A TIME.
//
// If a second meeting starts on the same licence, Zoom ends or takes over the
// first, and a real class is cut. So a room is created or re-timed only when
// the host is free at that time according to BOTH Kayfa's rooms and Zoom's own
// list, and a class is not started while the licence runs another meeting.
// Every refusal below is an ANSWER the operator has to read: which slot is
// taken, and what to do. None of them is ever shown as a raw code.

const refusalDetailOf = (err: any): any =>
  err && typeof err === 'object' && err.detail && typeof err.detail === 'object'
    ? err.detail
    : null;

const HOST_REFUSAL_CODE_LIST: HostRefusalCode[] = ['host_busy', 'host_check_unavailable', 'host_lock_busy'];

/**
 * Which of the three licence refusals this error is, or null. Read from the
 * 409's `code` first and from `detail.kind` second, so either side can carry it.
 */
export const hostRefusalCode = (err: any): HostRefusalCode | null => {
  const code = err && typeof err === 'object' ? err.code : undefined;
  const kind = refusalDetailOf(err)?.kind;
  return HOST_REFUSAL_CODE_LIST.find((c) => c === code || c === kind) || null;
};

export const hostBusyDetail = (err: any): HostBusyDetail | null => {
  const d = refusalDetailOf(err);
  return d?.kind === 'host_busy' ? (d as HostBusyDetail) : null;
};

export const hostCheckUnavailableDetail = (err: any): HostCheckUnavailableDetail | null => {
  const d = refusalDetailOf(err);
  return d?.kind === 'host_check_unavailable' ? (d as HostCheckUnavailableDetail) : null;
};

/** The R2 start refusal (`detail.kind === 'start_blocked'`), or null. */
export const startBlockedDetail = (err: any): StartBlockedDetail | null => {
  const d = refusalDetailOf(err);
  return d?.kind === 'start_blocked' ? (d as StartBlockedDetail) : null;
};

// ── when ───────────────────────────────────────────────────────────────────

const formatIn = (iso: string, opts: Intl.DateTimeFormatOptions, tz?: string | null): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  try {
    return new Intl.DateTimeFormat('en-GB', { ...opts, timeZone: tz || undefined }).format(d);
  } catch {
    // An unknown zone name must not blank the sentence — the browser's zone is
    // a worse answer than the batch's, but a far better one than nothing.
    return new Intl.DateTimeFormat('en-GB', opts).format(d);
  }
};

const DAY_TIME: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};
const TIME_ONLY: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
const DAY_ONLY: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

/** "18:05" in the batch's zone. */
export const busyTime = (iso?: string | null, tz?: string | null): string =>
  iso ? formatIn(iso, TIME_ONLY, tz) : '';

/**
 * "on Mon 5 Oct, 18:00–19:00 (Africa/Cairo)" for one busy slot. A slot with
 * no fixed time is "at any time" — that is what Zoom's type-3 meeting means.
 */
export const busyWhen = (
  slot?: { startsAt?: string | null; endsAt?: string | null } | null,
  tz?: string | null,
): string => {
  if (!slot?.startsAt) return 'at any time';
  const start = formatIn(slot.startsAt, DAY_TIME, tz);
  let end = '';
  if (slot.endsAt) {
    const sameDay = formatIn(slot.startsAt, DAY_ONLY, tz) === formatIn(slot.endsAt, DAY_ONLY, tz);
    end = formatIn(slot.endsAt, sameDay ? TIME_ONLY : DAY_TIME, tz);
  }
  return `on ${start}${end ? `–${end}` : ''}${tz ? ` (${tz})` : ''}`;
};

// ── the chip on each busy slot ─────────────────────────────────────────────

const BUSY_SOURCE_LABEL: Record<string, string> = {
  kayfa_room: 'Room on this licence',
  kayfa_timetable: 'Kayfa class',
  zoom: 'Outside Kayfa',
  zoom_recurring: 'Repeating',
  zoom_no_fixed_time: 'No fixed time',
  zoom_live: 'Live now',
  same_class_elsewhere: 'This class, elsewhere',
  kayfa_starting: 'Started a moment ago',
};

const BUSY_SOURCE_STYLE: Record<string, ZoomChipStyle> = {
  kayfa_room: { color: 'info', variant: 'ghost' },
  kayfa_timetable: { color: 'info', variant: 'outlined' },
  zoom: { color: 'warning', variant: 'ghost' },
  zoom_recurring: { color: 'warning', variant: 'ghost' },
  zoom_no_fixed_time: { color: 'error', variant: 'ghost' },
  zoom_live: { color: 'error', variant: 'ghost' },
  same_class_elsewhere: { color: 'error', variant: 'ghost' },
  kayfa_starting: { color: 'error', variant: 'ghost' },
};

export const busySourceLabel = (source?: string | null): string => {
  if (!source) return 'Busy';
  return BUSY_SOURCE_LABEL[source] || humanise(source);
};

export const busySourceStyle = (source?: string | null): ZoomChipStyle =>
  (source && BUSY_SOURCE_STYLE[source]) || { color: 'warning', variant: 'outlined' };

// ── the sentences ──────────────────────────────────────────────────────────

const quoted = (s?: string | null): string => (s ? `“${s}”` : '');

/** One sentence per taken slot, exactly as §3.6 writes it. */
export const hostBusyConflictLine = (
  c: HostBusyConflict,
  hostEmail?: string | null,
  tz?: string | null,
): string => {
  const host = hostEmail || 'this Zoom host';
  const when = busyWhen(c, tz);
  const cohort = [c.diplomaName, c.batchName].filter(Boolean).join(' — ');
  const kayfaClass = `${quoted(c.title) || 'A Kayfa class'}${cohort ? ` (${cohort})` : ''}`;
  const topic = quoted(c.topicMasked);
  switch (c.source) {
    case 'kayfa_room':
      return `${kayfaClass} already has a Zoom room on ${host} ${when}.`;
    case 'kayfa_timetable':
      return `${kayfaClass} is scheduled on ${host} ${when}.`;
    case 'zoom': {
      const what = [topic, c.meetingIdMasked].filter(Boolean).join(', ');
      return `A meeting outside Kayfa${what ? ` (${what})` : ''} is booked on ${host} ${when}.`;
    }
    case 'zoom_recurring':
      return `A repeating meeting outside Kayfa${topic ? ` (${topic})` : ''} has an occurrence on ${host} ${when}.`;
    case 'zoom_no_fixed_time':
      return `${host} has a Zoom meeting with no fixed time${topic ? ` (${topic})` : ''}. Zoom lets it start at any moment, so Kayfa books nothing on this licence while it exists.`;
    case 'zoom_live':
      return `A meeting is live on ${host} right now${c.startsAt ? ` (since ${busyTime(c.startsAt, tz)})` : ''}.`;
    case 'same_class_elsewhere':
      return 'Zoom already holds a room for this class that this Kayfa system did not record (another Kayfa system, or an interrupted attempt). Kayfa will not make a second one.';
    case 'kayfa_starting':
      return `${kayfaClass} was started on ${host} a moment ago — Zoom may not list it as live yet.`;
    default:
      return `Another meeting is booked on ${host} ${when}.`;
  }
};

export const HOST_BUSY_FIX_LINE =
  'Move this class, give the batch another Zoom host, or remove the other meeting in Zoom. No room was created.';

export const HOST_LOCK_BUSY_TEXT =
  'Another change on this Zoom host is being saved right now. Try again in a few seconds. Nothing was changed.';

const HOST_CHECK_REASON_LABEL: Record<string, string> = {
  timeout: 'Zoom did not answer in time',
  deadline: 'the check ran out of time',
  network_error: 'Zoom could not be reached',
  zoom_unavailable: 'Zoom answered with a server error',
  rate_limited: 'Zoom is rate limiting this account',
  bad_credentials: 'Zoom rejected the account’s credentials',
  not_activated: 'the Zoom app is not activated',
  insufficient_scopes: 'the Zoom app is missing a permission',
  key_unavailable: 'the account’s secret could not be read',
  not_configured: 'the Zoom account is not configured',
  truncated: 'the licence has more meetings than Kayfa can read at once',
  expand_cap: 'too many repeating meetings to read',
  expand_incomplete: 'a repeating meeting’s dates could not all be read',
  time_unparseable: 'Zoom sent a meeting time Kayfa could not read',
  db_truncated: 'too many Kayfa classes on this host to check',
  moved: 'the class was moved while Kayfa was checking',
  unknown: 'Zoom sent an unexpected answer',
};

export const hostCheckReasonLabel = (reason?: string | null): string => {
  if (!reason) return 'Zoom did not answer';
  return HOST_CHECK_REASON_LABEL[reason] || humanise(reason).toLowerCase();
};

export const hostCheckUnavailableText = (hostEmail?: string | null, reason?: string | null): string =>
  `Kayfa could not ask Zoom whether ${hostEmail || 'this Zoom host'} is free (${hostCheckReasonLabel(
    reason,
  )}). No room was created — a room booked without that check could cut a running class. Try again in a few minutes.`;

/** What was being done when the licence said no. Changes the title and the way out. */
export type HostRefusalAction = 'create' | 'sync' | 'move' | 'schedule' | 'restore' | 'generate';

const HOST_BUSY_TITLE: Record<HostRefusalAction, string> = {
  create: 'No Zoom room was created — the host is busy then',
  sync: 'The new time was not sent to Zoom — the host is busy then',
  move: 'This room was NOT moved',
  schedule: 'This class cannot move there',
  restore: 'This class cannot be restored',
  generate: 'This schedule would double-book the Zoom host',
};

const HOST_BUSY_FIX: Record<HostRefusalAction, string> = {
  create: HOST_BUSY_FIX_LINE,
  sync:
    'Move this class, give the batch another Zoom host, or remove the other meeting in Zoom. Zoom keeps this room’s old time until then.',
  move: 'The batch’s new host is busy at this class’s time. The class keeps its current room — move the class, or pick another host.',
  schedule:
    'Pick another time, give the batch another Zoom host, or remove the other meeting in Zoom. Nothing was changed.',
  restore:
    'Move this class or the other one, or give the batch another Zoom host, then restore it. The class stays cancelled.',
  generate:
    'Move the clashing classes, give the batch another Zoom host, or remove the other meeting in Zoom. Nothing was changed.',
};

/** Everything a licence refusal dialog shows, built once from the 409. */
export type HostRefusalView = {
  code: HostRefusalCode;
  title: string;
  // Sentences for the top of the dialog. One per taken slot for host_busy.
  lines: string[];
  // host_busy only: the taken slots, for the chips.
  conflicts: HostBusyConflict[];
  hostEmail: string;
  bufferMinutes: number;
  // The way out, always non-empty.
  fix: string;
  // true when Zoom itself was asked (false = Kayfa's own timetable only).
  zoomChecked: boolean | null;
};

/**
 * Turn a 409 into what the operator reads. Null when the error is not one of
 * the three licence refusals. When the BE sent no `detail` (or one this app
 * does not know), its own `messages[]` are shown — never the bare code.
 */
export const hostRefusalView = (
  err: any,
  action: HostRefusalAction,
  tz?: string | null,
): HostRefusalView | null => {
  const code = hostRefusalCode(err);
  if (!code) return null;
  const beLines = zoomMessages(err, '').filter(Boolean);

  if (code === 'host_lock_busy') {
    return {
      code,
      title: 'Try again in a few seconds',
      lines: [HOST_LOCK_BUSY_TEXT],
      conflicts: [],
      hostEmail: '',
      bufferMinutes: 0,
      fix: 'Nothing was changed. Press the same button again in a moment.',
      zoomChecked: null,
    };
  }

  if (code === 'host_check_unavailable') {
    const d = hostCheckUnavailableDetail(err);
    const hostEmail = d?.hostEmail || '';
    return {
      code,
      title: 'Kayfa could not check the Zoom host',
      lines: d ? [hostCheckUnavailableText(hostEmail, d.reason)] : beLines.length ? beLines : [hostCheckUnavailableText(hostEmail, '')],
      conflicts: [],
      hostEmail,
      bufferMinutes: 0,
      fix:
        action === 'move'
          ? 'The class keeps its current room. Try the move again in a few minutes.'
          : 'Nothing was changed. Try again in a few minutes; if it keeps failing, test the Zoom account in Settings › Zoom.',
      zoomChecked: false,
    };
  }

  const d = hostBusyDetail(err);
  const conflicts = (d?.conflicts || []).filter(Boolean);
  const hostEmail = d?.hostEmail || '';
  const lines = conflicts.length
    ? conflicts.map((c) => hostBusyConflictLine(c, hostEmail, tz))
    : beLines.length
    ? beLines
    : [`${hostEmail || 'This Zoom host'} is already booked at this class’s time.`];
  return {
    code,
    title: HOST_BUSY_TITLE[action],
    lines,
    conflicts,
    hostEmail,
    bufferMinutes: Number(d?.bufferMinutes) || 0,
    fix: HOST_BUSY_FIX[action],
    zoomChecked: typeof d?.checked?.zoom === 'boolean' ? d.checked.zoom : null,
  };
};

/**
 * The HOST_BUSY row reason, read from a 200 create outcome that carried the
 * check instead of throwing (the preflight shape). Same view as the 409.
 */
export const hostRefusalFromOutcome = (
  outcome: any,
  action: HostRefusalAction,
  tz?: string | null,
): HostRefusalView | null => {
  const detail = outcome?.hostCheck;
  if (!detail || typeof detail !== 'object') return null;
  return hostRefusalView({ code: detail.kind, detail, messages: [] }, action, tz);
};

// ── start (R2) ─────────────────────────────────────────────────────────────

const startBlockerLabel = (d: StartBlockedDetail): string => {
  if (d.isKayfa) {
    return `${quoted(d.title) || 'another Kayfa class'}${d.cohort ? `, ${d.cohort}` : ''}`;
  }
  return `a meeting outside Kayfa${d.meetingIdMasked ? `, ${d.meetingIdMasked}` : ''}`;
};

/**
 * The admin start refusal, in words. '' for a reason this app has no sentence
 * for — the caller then shows the BE's own messages.
 */
export const startBlockedText = (d: StartBlockedDetail, tz?: string | null): string => {
  const host = d.hostEmail || 'this class’s Zoom account';
  switch (d.reason) {
    case 'host_live_elsewhere':
      return `Another meeting is live on ${host}${
        d.liveSinceIso ? ` since ${busyTime(d.liveSinceIso, tz)}` : ''
      } (${startBlockerLabel(d)}). Starting this class now would end or take over that meeting.`;
    case 'host_check_unavailable':
      return `Kayfa could not confirm with Zoom that ${host} is free, so the class was not started. Try again in a minute.`;
    case 'class_live_hosted_elsewhere':
      return 'This class is already being hosted from another device. Starting it here would take the host role away from them.';
    case 'host_slot_taken':
      if (d.phase === 'late') {
        return `This class’s time is over, and ${startBlockerLabel(d)} is booked on ${host}${
          d.slotStartsIso ? ` from ${busyTime(d.slotStartsIso, tz)}` : ' soon'
        }. A class started now would run into it. Move this class to another time instead.`;
      }
      return `Another class is scheduled on ${host} right now (${startBlockerLabel(d)}${
        d.slotEndsIso ? `, until ${busyTime(d.slotEndsIso, tz)}` : ''
      }). Starting this class now would cut it off.${
        d.opensAtIso ? ` This class can start from ${busyTime(d.opensAtIso, tz)}.` : ''
      }`;
    case 'host_starting':
      return 'Another class on this Zoom account was started a moment ago. Starting now would cut it off.';
    case 'too_late':
      return 'This class’s time has passed. Reopen it first if it still needs to be taught.';
    default:
      return '';
  }
};

export const startBlockedTitle = (d: StartBlockedDetail): string =>
  d.reason === 'host_check_unavailable'
    ? 'Kayfa could not check the Zoom account'
    : d.reason === 'too_late'
    ? 'This class’s time has passed'
    : 'The Zoom account is busy';
